import {Link, useLocation, useNavigate} from "react-router-dom";
import {
  addMinutes,
  adjustFromTimeZone,
  adjustToTimeZone,
  Dispatch,
  DispatchRequestEntity, DispatchRequestStatus,
  dispatchRequestStatus,
  dispatchStatus,
  getNextQuarterHour,
  getObjectId,
  isAfter,
  isEqual,
  Quantity,
  transformDispatchRequest,
  useGetDispatchRequestQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {notifications} from "@mantine/notifications";
import {
  DefaultText,
  DispatchForm,
  DispatchFormOptions,
  DispatchFormValues,
  EntityCommentList,
  ErrorPage,
  FormattedDatabaseCode,
  FormattedDateTime,
  getTransportCategoryColorRefByType,
  LoadingPage,
  ThemedPage,
  useApp,
  useAuth,
  useDispatchRequestMutation,
} from "@ots-frontend/ots-ui";
import {
  Alert,
  Anchor,
  Badge,
  Card,
  Center,
  CloseButton,
  Container,
  Divider,
  Grid,
  Group,
  Paper,
  Space,
  Text,
  Title
} from "@mantine/core";
import {DispatchRequestCard} from "../../components/DispatchRequestCard/DispatchRequestCard";

type DispatchRequestDispatchPageProps = {
  id?: string;
  title: string
  expectedStatus: DispatchRequestStatus
  isSubmitting?: boolean
  onSubmit: (dispatchRequestId: string, dispatch: Partial<Dispatch> | undefined, copyDispatchRequestComments: boolean | undefined, onSuccess: (() => void) | undefined) => void
}

const toFormOptions = (request: DispatchRequestEntity): Partial<DispatchFormOptions> => {
  return {
    pickupSiteSelectionDisabled: typeof request.pickupSiteId !== "undefined",
    pickupWellSelectionDisabled: typeof request.pickupWellId !== "undefined",
    pickupContainerSelectionDisabled: typeof request.pickupContainerId !== "undefined",
    deliverySiteSelectionDisabled: typeof request.deliverySiteId !== "undefined",
    deliveryWellSelectionDisabled: typeof request.deliveryWellId !== "undefined",
    deliveryContainerSelectionDisabled: typeof request.deliveryContainerId !== "undefined",
    loadDetailsDisabled: true,
    categoryDisabled: true,
    copyDispatchRequestCommentsVisible: true,
  };
};

const toFormDefaults = (request: DispatchRequestEntity): Partial<DispatchFormValues> => {
  let date: Date | undefined = undefined;
  let quantity: Quantity | undefined = undefined;

  const assocs = request.dispatchRequestAssocList ?? [];

  const maxDttm = assocs
    .map(a => a.dispatchDttm)
    .filter((dttm): dttm is number => !!dttm)
    .reduce((a, b) => Math.max(a, b), -Infinity);

  let dttm: number | undefined = undefined;
  if (maxDttm !== -Infinity) {
    dttm = addMinutes(maxDttm, 15).getTime();
  } else if (request.scheduledDttm) {
    dttm = getNextQuarterHour(request.scheduledDttm).getTime();
  }

  if (dttm) {
    const adjustedDate = adjustFromTimeZone(dttm);
    const nowDate = new Date();
    if (isEqual(adjustedDate, nowDate) || isAfter(adjustedDate, nowDate)) {
      date = adjustedDate;
    }
  }

  if (request.quantity && request.quantityUom) {
    quantity = {
      number: request.quantity,
      units: request.quantityUom
    };
  }
  return {
    date: date ?? getNextQuarterHour(),
    quantity: quantity,
    freightType: request.freightTypeId,
    serviceType: request.serviceTypeId,
    pickup: {
      site: request.pickupSiteId,
      well: request.pickupWellId,
      container: request.pickupContainerId,
    },
    delivery: {
      site: request.deliverySiteId,
      well: request.deliveryWellId,
      container: request.deliveryContainerId,
    },
    workOrderNumber: request.workOrderNumber ?? request.externalId,
    comments: undefined,
    category: request.category,
    copyDispatchRequestComments: true,
  };
};

export const DispatchRequestDispatchPage = (props: DispatchRequestDispatchPageProps) => {
  const {id, title, expectedStatus, onSubmit, isSubmitting = false} = props;

  const navigate = useNavigate();
  const {userId} = useAuth()
  const {regionId} = useApp()

  const {dispatchRequest, isFetching} = useGetDispatchRequestQuery(id ?? skipToken, {
    selectFromResult: ({data, isFetching}) => ({
      dispatchRequest: data ? transformDispatchRequest(data) : undefined,
      isFetching: isFetching
    })
  });

  const formOptions: Partial<DispatchFormOptions> | undefined = useMemo(() => {
    return dispatchRequest ? toFormOptions(dispatchRequest) : undefined;
  }, [dispatchRequest]);

  const formDefaults: Partial<DispatchFormValues> | undefined = useMemo(() => {
    return dispatchRequest ? toFormDefaults(dispatchRequest) : undefined;
  }, [dispatchRequest]);

  const onDispatchSubmit = useCallback((formValues: DispatchFormValues) => {
    if (!dispatchRequest) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "Dispatch Request Not Found"
      });
      return;
    }

    const dispatch: Partial<Dispatch> = {
      createdById: userId,
      deviceId: "0",
      type: "PICKUP",
      regionId: regionId,
      status: dispatchStatus.pending.code,
      scheduleDttm: adjustToTimeZone(formValues.date).getTime(),
      personId: getObjectId(formValues.person),
      trailerId: getObjectId(formValues.trailer),
      vehicleId: getObjectId(formValues.vehicle),
      quantityDecimal: formValues.quantity.number,
      quantityUnitOfMeasure: formValues.quantity.units,
      waterTypeId: dispatchRequest.freightTypeId ?? getObjectId(formValues.freightType),
      pickupServiceTypeId: dispatchRequest.serviceTypeId ?? getObjectId(formValues.serviceType),
      deliveryServiceTypeId: dispatchRequest.serviceTypeId ?? getObjectId(formValues.serviceType),
      pickupSiteId: dispatchRequest.pickupSiteId ?? getObjectId(formValues.pickup?.site),
      pickupWellId: dispatchRequest.pickupWellId ?? getObjectId(formValues.pickup?.well),
      pickupContainerId: dispatchRequest.pickupContainerId ?? getObjectId(formValues.pickup?.container),
      deliverySiteId: dispatchRequest.deliverySiteId ?? getObjectId(formValues.delivery?.site),
      deliveryWellId: dispatchRequest.deliveryWellId ?? getObjectId(formValues.delivery?.well),
      deliveryContainerId: dispatchRequest.deliveryContainerId ?? getObjectId(formValues.delivery?.container),
      authorizeId: dispatchRequest.authorizeId,
      operatorId: dispatchRequest.operatorId,
      workOrderNumber: formValues.workOrderNumber,
      comments: formValues.comments,
    };

    onSubmit(dispatchRequest.id, dispatch, formValues.copyDispatchRequestComments, () => navigate(-1))
  }, [dispatchRequest, regionId, userId, onSubmit, navigate]);


  if (isFetching) {
    return (
      <LoadingPage/>
    )
  }

  if (!dispatchRequest) {
    return (
      <ErrorPage message={"Dispatch Request Not Found"}/>
    );
  }

  if (dispatchRequest.status !== expectedStatus) {
    return (
      <ErrorPage message={"Dispatch Request Already Resolved"}/>
    );
  }

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            <Paper w="100%" shadow="sm" p="md" py="md">
              <Group position="apart">
                <Title align="center">
                  {title}
                </Title>
                <CloseButton title="Close" size="xl" iconSize={20} onSubmit={() => navigate(-1)}/>
              </Group>
              <Space h="md"/>
              <Grid>
                <Grid.Col span={8}>
                  <Container fluid={true}>
                    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                      <DispatchForm
                        options={formOptions}
                        defaultValues={formDefaults}
                        submitting={isSubmitting}
                        onSubmit={onDispatchSubmit}/>
                    </Card>
                  </Container>
                </Grid.Col>
                <Grid.Col span={4}>
                  <DispatchRequestCard
                    shadow="sm"
                    padding="lg"
                    mb="lg"
                    radius="md"
                    withBorder
                    dispatchRequest={dispatchRequest} />
                </Grid.Col>
              </Grid>
            </Paper>
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};
